import React, { useState, useEffect, useRef } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  IconButton,
  Button,
  TextField,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Grid,
} from '@material-ui/core';
import {
  Close as CloseIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  Add as AddIcon
} from '@material-ui/icons';

import api from 'utils/api';
import { toast, showGlobalLoader } from 'utils/helpers';
import { useBimViewer } from './useBimViewer';
import DocumentListDialog from './DocumentListDialog';

const DocumentViewer = (props) => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedValue, setSelectedValue] = useState();
  const [addDocumentDialogOpen, setAddDocumentDialogOpen] = useState(false);
  const [loadedDocumentIds, setLoadedDocumentIds] = useState([]);
  const [mainDocumentId, setMainDocumentId] = useState(null);

  const {
    containerRef,
    initViewer,
    resetViewer,
    loadIfc,
    removeModel,
    applyClassifications,
    groupedData,
    selectedElements,
    selectionMeasurements,
    selectionProperties,
    onFilterItemClick,
    handleSelectionClear: handleBimSelectionClear,
  } = useBimViewer();

  const fetchDocuments = async () => {
    const docIds = props.documentIds || [];
    const docs = props.documents || (props.document ? [props.document] : []);

    let initialDocsToLoad = [];

    if (docs.length > 0) {
      initialDocsToLoad = docs;
    } else if (docIds.length > 0) {
       // We have IDs but need objects (at least for names/project ID if possible)
       // But wait, we fetch content by ID anyway.
       // We should try to fetch metadata for at least one to get project ID?
       // For now, let's just proceed with loading by ID.
       initialDocsToLoad = docIds.map(id => ({ _id: id }));
    }

    if (initialDocsToLoad.length === 0) return;

    if (initialDocsToLoad.length > 0) {
      setMainDocumentId(initialDocsToLoad[0]._id);
    }

    // Show loader initially to cover the whole process if possible, 
    // though loadIfc might toggle it.
    showGlobalLoader(true);

    const loadedIds = [];

    for (const doc of initialDocsToLoad) {
      if (loadedDocumentIds.includes(doc._id)) continue;
      
      try {
        const { data } = await api.get(`/documents/${doc._id}/contents`, { responseType: 'arraybuffer' });
        // loadIfc handles its own error toast for parsing, but we handle fetch error here
        await loadIfc(data, doc._id);
        loadedIds.push(doc._id);
      } catch (err) {
        console.error(err);
        toast(t('Klaida įkeliant įrašą') + (doc.name ? `: ${doc.name}` : ''), 'error');
      }
    }
    
    setLoadedDocumentIds(prev => [...prev, ...loadedIds]);

    await applyClassifications();
    showGlobalLoader(false);
  }

  useEffect(() => {
    initViewer()
      .then(() => {
        fetchDocuments();
      });
  }, []);

  const handleClose = () => props.onClose();

  const handleSearch = (text) => {
    console.log('search', text);
    setSearchText(text);
  };

  const handleSelectionClear = (e) => {
    e.preventDefault();
    handleBimSelectionClear();
    setSelectedValue(null);
  }

  const handleValueSelection = (name, value) => {
    setSelectedValue({ name, value });
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    props.onValueSelect(selectedValue);
    handleClose();
  }
  
  const handleAddDocument = () => {
      setAddDocumentDialogOpen(true);
  };

  const handleDocumentsSelected = async (selectedDocs) => {
      showGlobalLoader(true);
      
      // Full reset and reload to avoid ghost data issues
      resetViewer();
      await initViewer();

      const newLoadedIds = [];
      if (selectedDocs && selectedDocs.length > 0) {
        for (const doc of selectedDocs) {
            try {
                const { data } = await api.get(`/documents/${doc._id}/contents`, { responseType: 'arraybuffer' });
                await loadIfc(data, doc._id);
                newLoadedIds.push(doc._id);
            } catch (err) {
                console.error(err);
                toast(t('Klaida įkeliant įrašą') + `: ${doc.name}`, 'error');
            }
        }
      }
      
      setLoadedDocumentIds(newLoadedIds);
      await applyClassifications();
      showGlobalLoader(false);
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* 3D scene */}
      <div
        ref={containerRef}
        style={props.style || {
          width: '100%',
          height: 'calc(100vh - 150px)',
        }}
      >
      </div>

      {/* Top Controls */}
      <div style={{ position: 'absolute', top: 0, right: 0 }}>
        {!!props.onClose && (
          <IconButton onClick={() => handleClose()}>
            <CloseIcon />
          </IconButton>
        )}
      </div>

      {/* Data filter */}
      <div className="documentviewer-filter-wrapper" style={{ paddingBottom: 45 }}>
        {/* Search input */}
        <TextField
          variant="outlined"
          fullWidth
          size="small"
          margin="normal"
          value={searchText}
          onChange={e => handleSearch(e.target.value)}
          placeholder={t("Paieška")}
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" />,
            endAdornment: (
              <IconButton
                title={t('Išvalyti')}
                aria-label="Clear"
                size="small"
                style={{ visibility: !!searchText ? 'visible' : 'hidden' }}
                onClick={() => handleSearch('')}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            ),
          }}
          style={{ paddingBottom: 5, marginTop: 0 }}
        />

        {/* Data structures */}
        <div>
          <Tabs
            value={activeTab}
            onChange={(_e, newVal) => setActiveTab(newVal)}
            scrollButtons="auto"
            variant="scrollable"

          >
            {groupedData.map((group, index) => (
              <Tab key={index} label={group.data.name} />
            ))}
          </Tabs>
        </div>
        <div className="documentviewer-filter-list-wrapper">
          <TreeView
            data={groupedData[activeTab]?.children || []}
            onItemClick={onFilterItemClick}
            searchQuery={searchText}
          />
        </div>

        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, textAlign: 'center' }}>
          <Button onClick={handleAddDocument} title={t('Pridėti dokumentą')} variant="contained" color="primary" size="small" startIcon={<AddIcon />} style={{ marginBottom: 10 }}>
            {t('Prijungti kitus dokumentus')}
          </Button>
        </div>
      </div>

      {!!selectedElements && (
        <div
          className="documentviewer-selection-wrapper"
          style={!props.onValueSelect ? { paddingBottom: '0px' } : {}}
        >
          <div>{t('Pasirinkta elementų')}: {selectionMeasurements.count} <a href="#" onClick={handleSelectionClear}><small>({t('Išvalyti')})</small></a></div>

          <TableContainer style={{ maxHeight: '400px' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {!!props.onValueSelect && <TableCell />}
                  <TableCell>{t('Savybė')}</TableCell>
                  <TableCell align="right">{t('Reikšmė')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>

                {/* Element count */}
                <TableRow>
                  {!!props.onValueSelect && <TableCell align="center">
                    <input checked={selectedValue?.name === 'count'} type="radio" name="selection" onChange={() => handleValueSelection('count', selectionMeasurements.count)} />
                  </TableCell>}
                  <TableCell>{t('Elementų kiekis')}, {t('vnt.')}</TableCell>
                  <TableCell align="right">{selectionMeasurements.count}</TableCell>
                </TableRow>

                {/* Total volume */}
                <TableRow>
                  {!!props.onValueSelect && <TableCell align="center">
                    <input checked={selectedValue?.name === 'volume'} type="radio" name="selection" onChange={() => handleValueSelection('volume', selectionMeasurements.volume)} />
                  </TableCell>}
                  <TableCell>{t('Tūris')}, m<sup>3</sup></TableCell>
                  <TableCell align="right">{selectionMeasurements.volume}</TableCell>
                </TableRow>

                {!!selectionProperties && Object.keys(selectionProperties).map((name, index) => (
                  <TableRow key={index}>
                    {!!props.onValueSelect && <TableCell align="center">
                      <input
                        checked={selectedValue?.name === name}
                        type="radio"
                        name="selection"
                        onChange={() => handleValueSelection(name, selectionProperties[name])}
                        disabled={props.selectOnlyNumbers && !_.isNumber(selectionProperties[name])}
                      />
                    </TableCell>}
                    <TableCell>{name}</TableCell>
                    <TableCell align="right">{selectionProperties[name]}</TableCell>
                  </TableRow>
                ))}

              </TableBody>
            </Table>
          </TableContainer>

          {props.onValueSelect && (
            <Grid container spacing={2} className="value-submit">
              <Grid item xs={6}>
                <TextField
                  variant="outlined"
                  fullWidth
                  size="small"
                  margin="normal"
                  value={selectedValue?.value}
                  onChange={e => setSelectedValue({ ...selectedValue, value: e.target.value })}
                  style={{ padding: 0, margin: 0 }}
                />
              </Grid>
              <Grid item xs={6}>
                <Button
                  onClick={handleSubmit}
                  disabled={!selectedValue?.value}
                  size="small"
                  variant="contained"
                  color="primary"
                  fullWidth
                >
                  {t('Įkelti reikšmę')}
                </Button>
              </Grid>
            </Grid>
          )}
        </div>
      )}

      <DocumentListDialog
        open={addDocumentDialogOpen}
        onClose={() => setAddDocumentDialogOpen(false)}
        onSelect={handleDocumentsSelected}
        loadedDocumentIds={loadedDocumentIds}
        mainDocumentId={mainDocumentId}
      />
    </div >
  )
}

const TreeNode = ({ node, onItemClick, searchQuery = '' }) => {

  const onClick = (e) => {
    e.preventDefault();

    if (onItemClick) onItemClick(node);
  }

  const children = node?.children?.filter(child => !child?.children?.length && searchQuery ? child?.data?.name?.toLowerCase().includes(searchQuery?.toLowerCase()) : true) || [];

  if (searchQuery && !node?.data?.name?.toLowerCase().includes(searchQuery?.toLowerCase()) && children.length === 0) {
    return null;
  }

  return (
    <li>
      <a href="#" onClick={onClick}>{node?.data?.name || 'Unnamed Node'}</a>
      {children.length > 0 && (
        <ul className="documentviewer-filter-list">
          {children.map((child, i) => (
            <TreeNode key={i} node={child} onItemClick={onItemClick} searchQuery={searchQuery} />
          ))}
        </ul>
      )}
    </li>
  )
};

const TreeView = ({ data, onItemClick, searchQuery = '' }) => (
  <ul className="documentviewer-filter-list">
    {data
      .filter((node) => !node?.children?.length && searchQuery ? node?.data?.name?.toLowerCase().includes(searchQuery?.toLowerCase()) : true)
      .map((node, i) => (
        <TreeNode key={i} node={node} onItemClick={onItemClick} searchQuery={searchQuery} />
      ))}
  </ul>
);

export default DocumentViewer;